import { useReducer, useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks'
import type { SearchResult, PaletteView, AskAIResponse, ContentPage, Suggestion } from '../types'
import { MOCK_SUGGESTIONS } from '../mock/data'
import { TriggerBar } from './TriggerBar'
import { Palette } from './Palette'
import { useKeyboard } from '../hooks/useKeyboard'
import { getCache, setCache } from '../lib/cache'
import { createTelemetry } from '../lib/telemetry'
import { getRecentVisits } from '../lib/recentPages'

type WidgetPlacement = 'bottom-center' | 'bottom-right' | 'bottom-left' | 'top-center' | 'top-right' | 'top-left'

interface ProjectConfig {
  primaryColor?: string
  placement?: WidgetPlacement
  placeholder?: string
  aiEnabled?: boolean
  suggestions?: Suggestion[]
  hideSuggestions?: boolean
  suggestionsMode?: 'global' | 'per-page'  // placeholder for Phase 3 per-page generation
}

interface AppProps {
  projectId: string
  apiBase: string
  position?: WidgetPlacement
}

type CacheStatus = 'cold' | 'warm' | 'synced'

interface AppState {
  isOpen: boolean
  query: string
  view: PaletteView
  results: SearchResult[]
  pages: ContentPage[]
  aiResponse: AskAIResponse | null
  cacheStatus: CacheStatus
  config: ProjectConfig
}

type AppAction =
  | { type: 'OPEN' }
  | { type: 'CLOSE' }
  | { type: 'SET_QUERY'; payload: string }
  | { type: 'SET_PAGES'; payload: ContentPage[] }
  | { type: 'SET_CACHE_STATUS'; payload: CacheStatus }
  | { type: 'SET_CONFIG'; payload: ProjectConfig }
  | { type: 'ASK_AI' }
  | { type: 'SET_ANSWER'; payload: AskAIResponse }
  | { type: 'START_FEEDBACK' }
  | { type: 'BACK' }

function scoreResult(page: ContentPage, q: string): number {
  const lower = q.toLowerCase()
  const title = page.title.toLowerCase()
  const summary = page.summary.toLowerCase()
  const content = page.content.toLowerCase()
  let score = 0
  if (title === lower) score += 3
  else if (title.includes(lower)) score += 2
  if (summary.includes(lower)) score += 1
  if (content.includes(lower)) score += 0.5
  return score
}

function search(query: string, pages: ContentPage[]): SearchResult[] {
  const q = query.trim()
  if (!q) return []
  return pages
    .map((p) => ({ page: p, score: scoreResult(p, q) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 8)
    .map(({ page }) => ({
      id: page.id,
      title: page.title,
      summary: page.summary,
      url: page.url,
      content: page.content,
    }))
}

function reducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'OPEN':
      return { ...state, isOpen: true }
    case 'CLOSE':
      return { ...state, isOpen: false, query: '', view: 'empty', results: [], aiResponse: null }
    case 'SET_QUERY': {
      const q = action.payload
      if (!q) return { ...state, query: '', view: 'empty', results: [], aiResponse: null }
      return { ...state, query: q, view: 'results', results: search(q, state.pages), aiResponse: null }
    }
    case 'SET_PAGES':
      return { ...state, pages: action.payload }
    case 'SET_CONFIG':
      return { ...state, config: { ...state.config, ...action.payload } }
    case 'SET_CACHE_STATUS':
      return { ...state, cacheStatus: action.payload }
    case 'ASK_AI':
      return { ...state, view: 'asking' }
    case 'SET_ANSWER':
      return { ...state, view: 'answer', aiResponse: action.payload }
    case 'START_FEEDBACK':
      return { ...state, view: 'feedback' }
    case 'BACK':
      return { ...state, view: 'empty' }
    default:
      return state
  }
}

const initialState: AppState = {
  isOpen: false,
  query: '',
  view: 'empty',
  results: [],
  pages: [],
  aiResponse: null,
  cacheStatus: 'cold',
  config: {},
}

export function App({ projectId, apiBase, position = 'bottom-center' }: AppProps) {
  const [state, dispatch] = useReducer(reducer, initialState)
  const [recentVisitUrls] = useState(() => getRecentVisits().map((v) => v.url))
  const [aiPending, setAiPending] = useState(false)

  const fireEvent = useMemo(
    () => createTelemetry(projectId, apiBase),
    [projectId, apiBase],
  )

  const open = useCallback(() => {
    dispatch({ type: 'OPEN' })
    fireEvent('open')
  }, [fireEvent])

  const close = useCallback(() => {
    dispatch({ type: 'CLOSE' })
    fireEvent('close')
  }, [fireEvent])

  const setQuery = useCallback(
    (q: string) => dispatch({ type: 'SET_QUERY', payload: q }),
    [],
  )

  // Debounced search telemetry (1s after last keystroke)
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (!state.query) return
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    searchTimerRef.current = setTimeout(() => {
      fireEvent('search', { query: state.query })
    }, 1000)
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    }
  }, [state.query, fireEvent])

  useKeyboard(open, close, state.isOpen)

  const handleAskAI = useCallback(
    (query: string) => {
      dispatch({ type: 'ASK_AI' })
      fireEvent('ai_ask', { query })
      fetch(`${apiBase}/api/projects/${projectId}/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query }),
      })
        .then(async (r) => {
          if (r.status === 429) {
            dispatch({
              type: 'SET_ANSWER',
              payload: { answer: 'Too many requests — please try again in a moment.', citations: [] },
            })
            return
          }
          if (!r.ok) throw new Error(`HTTP ${r.status}`)
          const data: AskAIResponse = await r.json()
          dispatch({ type: 'SET_ANSWER', payload: data })
        })
        .catch(() => {
          dispatch({
            type: 'SET_ANSWER',
            payload: { answer: 'Sorry, I could not answer that right now.', citations: [] },
          })
        })
    },
    [projectId, apiBase, fireEvent],
  )

  const handleFeedbackSubmit = useCallback(
    async (text: string) => {
      fireEvent('feedback_submit', { text })
      try {
        await fetch(`${apiBase}/api/projects/${projectId}/feedback`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text }),
        })
      } catch {
        // best-effort — don't surface network errors to users
      }
    },
    [projectId, apiBase, fireEvent],
  )

  // Cache-first content loader
  useEffect(() => {
    let cancelled = false

    async function loadContent() {
      // Phase A: try IndexedDB cache first — instant render on warm starts
      const cached = await getCache(projectId)
      if (cancelled) return

      if (cached) {
        dispatch({ type: 'SET_PAGES', payload: cached.pages })
        dispatch({ type: 'SET_CACHE_STATUS', payload: 'warm' })
      }

      // Phase B: fetch version stamp (tiny request, no body)
      let serverVersion: string | null = null
      try {
        const vRes = await fetch(`${apiBase}/api/projects/${projectId}/version`)
        if (!cancelled && vRes.ok) {
          const vData = await vRes.json()
          serverVersion = vData.version ?? null
        }
      } catch {
        // network offline — skip re-fetch, cached data is still usable
      }

      if (cancelled) return

      // Phase C: re-fetch content only when necessary
      const needsRefetch = !cached || (serverVersion !== null && cached.version !== serverVersion)

      if (needsRefetch) {
        try {
          const cRes = await fetch(`${apiBase}/api/projects/${projectId}/content`)
          const cData = await cRes.json() as { pages: ContentPage[]; config?: ProjectConfig }
          if (cancelled) return
          if (Array.isArray(cData.pages)) {
            dispatch({ type: 'SET_PAGES', payload: cData.pages })
            dispatch({ type: 'SET_CACHE_STATUS', payload: 'synced' })
            if (serverVersion) {
              setCache(projectId, serverVersion, cData.pages)
            }
          }
          if (cData.config) {
            dispatch({ type: 'SET_CONFIG', payload: cData.config })
          }
        } catch (err) {
          console.error('[Spotlight] Content fetch error:', err)
          // cached data (if any) remains in state
        }
      } else {
        dispatch({ type: 'SET_CACHE_STATUS', payload: 'synced' })
      }
    }

    loadContent()
    return () => { cancelled = true }
  }, [projectId, apiBase])

  // Auto-trigger AI when search returns no results (700ms debounce)
  const aiDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (aiDebounceRef.current) clearTimeout(aiDebounceRef.current)
    const aiOn = state.config.aiEnabled !== false
    const noResults = aiOn && state.view === 'results' && state.results.length === 0 && Boolean(state.query.trim())
    if (noResults) {
      setAiPending(true)
      aiDebounceRef.current = setTimeout(() => {
        handleAskAI(state.query)
      }, 700)
    } else {
      setAiPending(false)
    }
    return () => {
      if (aiDebounceRef.current) clearTimeout(aiDebounceRef.current)
    }
  }, [state.query, state.results.length, state.view, state.config.aiEnabled, handleAskAI])

  const handleResultClick = useCallback(
    (url: string) => {
      fireEvent('result_click', { url })
    },
    [fireEvent],
  )

  const handleStartFeedback = useCallback(() => {
    dispatch({ type: 'START_FEEDBACK' })
    fireEvent('feedback_open')
  }, [fireEvent])

  const handleBack = useCallback(() => {
    dispatch({ type: 'BACK' })
  }, [])


  const { primaryColor, placement, placeholder, aiEnabled, suggestions, hideSuggestions } = state.config
  // Config placement takes priority over the script-tag data-position attribute
  const resolvedPosition = (placement ?? position) as WidgetPlacement

  const resolvedSuggestions = hideSuggestions ? [] : (suggestions ?? MOCK_SUGGESTIONS)

  // Match recent visit URLs against indexed pages (indexed-only: excludes private/auth pages)
  const recentPages = useMemo(
    () =>
      recentVisitUrls
        .map((url) => state.pages.find((p) => p.url === url))
        .filter((p): p is ContentPage => Boolean(p))
        .slice(0, 5),
    [recentVisitUrls, state.pages],
  )

  const accentStyle = primaryColor
    ? { '--spotlight-accent': primaryColor, '--spotlight-accent-hover': primaryColor } as Record<string, string>
    : undefined

  return (
    <div style={accentStyle}>
      <TriggerBar onOpen={open} position={resolvedPosition} label={placeholder} />
      {state.isOpen && (
        <Palette
          query={state.query}
          view={state.view}
          results={state.results}
          suggestions={resolvedSuggestions}
          recentPages={recentPages}
          aiResponse={state.aiResponse}
          onClose={close}
          onQueryChange={setQuery}
          onSelectSuggestion={setQuery}
          onAskAI={handleAskAI}
          onResultClick={handleResultClick}
          onBack={handleBack}
          onFeedbackSubmit={handleFeedbackSubmit}
          isLoading={state.cacheStatus === 'cold' && state.pages.length === 0}
          aiPending={aiPending}
          placeholder={placeholder}
          aiEnabled={aiEnabled !== false}
        />
      )}
    </div>
  )
}
